/**
 * Maps a carrier error code on a failed/undelivered text into a plain-English
 * reason an agent can act on — so a failed message shows "Landline — can't
 * receive texts" instead of a bare "Failed".
 *
 * `category` lets the UI signal what to do about it:
 *   - 'lead'    → the recipient number is the problem (landline, disconnected,
 *                 opted out). Stop texting this lead; nothing to fix on our end.
 *   - 'carrier' → the carrier filtered/blocked this message (spam heuristics,
 *                 rate cap). Often transient or content-related.
 *   - 'account' → a registration/config issue on the sending side that resolves
 *                 once setup completes. Not the agent's fault.
 *   - 'unknown' → unrecognized code; show a generic reason.
 *
 * No vendor names appear in any label — these strings are shown to agents.
 *
 * MIRROR: fex-platform-service/src/constants/sms-error-codes.constants.ts holds
 * the same code set server-side. Add a code to BOTH or this map falls back to a
 * generic "Delivery failed".
 */

export type SmsFailureCategory = 'lead' | 'carrier' | 'account' | 'unknown';

export interface SmsFailureReason {
  /** Short label for the badge/tooltip, e.g. "Landline — can't receive texts". */
  label: string;
  /** One-line guidance on what to do. */
  hint: string;
  category: SmsFailureCategory;
}

const REASONS: Record<string, SmsFailureReason> = {
  // ---- Recipient / lead problems (stop texting this number) ----
  '30003': {
    label: 'Phone unreachable',
    hint: "The phone is off, out of service, or out of coverage.",
    category: 'lead',
  },
  '30004': {
    label: 'Message blocked',
    hint: 'The recipient or their carrier is blocking messages to this number.',
    category: 'lead',
  },
  '30005': {
    label: 'Number not in service',
    hint: "This number doesn't exist or has been disconnected.",
    category: 'lead',
  },
  '30006': {
    label: "Landline — can't receive texts",
    hint: 'This is a landline or a number that cannot receive SMS. Try calling instead.',
    category: 'lead',
  },
  '21211': {
    label: 'Invalid phone number',
    hint: 'This number is not a valid format.',
    category: 'lead',
  },
  '21214': {
    label: 'Invalid phone number',
    hint: 'This number could not be reached.',
    category: 'lead',
  },
  '21610': {
    label: 'Recipient opted out',
    hint: 'This person replied STOP. You can no longer text them unless they opt back in.',
    category: 'lead',
  },
  '21614': {
    label: 'Not a mobile number',
    hint: 'This number cannot receive text messages.',
    category: 'lead',
  },

  // ---- Carrier filtering / limits ----
  '30007': {
    label: 'Filtered as spam',
    hint: 'The carrier flagged this message as spam. Vary the wording and avoid links/all-caps.',
    category: 'carrier',
  },
  '30026': {
    label: 'Filtered as spam',
    hint: 'The message content was flagged. Vary the wording and avoid links/all-caps.',
    category: 'carrier',
  },
  '30022': {
    label: 'Sending too fast',
    hint: 'Too many texts went out at once. Wait a minute and try again.',
    category: 'carrier',
  },
  '30023': {
    label: 'Daily texting limit reached',
    hint: "Today's texting limit was reached. It resets at 3 AM ET — texts to other numbers still send.",
    category: 'carrier',
  },
  '30027': {
    label: 'Daily texting limit reached',
    hint: "Today's texting limit was reached. It resets at 3 AM ET — texts to other numbers still send.",
    category: 'carrier',
  },
  '30410': {
    label: 'Carrier timeout',
    hint: 'The carrier was slow to respond. Try again shortly.',
    category: 'carrier',
  },
  '30038': {
    label: 'Filtered — looked like a passcode',
    hint: 'The message was filtered for resembling a one-time verification code. Reword it and retry.',
    category: 'carrier',
  },
  '30019': {
    label: 'Attachment too large',
    hint: 'The attachment is too large for this carrier. Send a smaller image (under 1 MB), or text a link instead.',
    category: 'carrier',
  },
  '30036': {
    label: 'Message expired',
    hint: 'The message expired before the carrier could deliver it. Retry, or call this lead instead.',
    category: 'carrier',
  },

  // ---- Sending-side setup (resolves once registration completes) ----
  '30034': {
    label: 'Texting not registered yet',
    hint: 'This number isn’t fully registered for texting yet. It resolves once registration finishes.',
    category: 'account',
  },
  '30032': {
    label: 'Number not verified yet',
    hint: 'This number isn’t verified for texting yet.',
    category: 'account',
  },
  '30002': {
    label: 'Account issue',
    hint: 'The messaging account has a problem. Contact support.',
    category: 'account',
  },
  '21606': {
    label: "Number can't send texts",
    hint: 'This number is not set up for texting. Contact support.',
    category: 'account',
  },
  '30035': {
    label: "Number can't text right now",
    hint: 'The sending number is still being set up for texting. Send from another of your numbers, or retry later (setup can take up to 24 hours).',
    category: 'account',
  },
  '63038': {
    label: 'Daily limit reached',
    hint: 'The account hit its daily message limit. It resets tomorrow.',
    category: 'account',
  },
  '30008': {
    label: 'Delivery failed',
    hint: 'The carrier rejected the message without a specific reason.',
    category: 'carrier',
  },
};

/**
 * Resolve a friendly reason for a failed text.
 * @param errorCode  carrier error code stored on the record (may be null)
 * @param errorMessage  raw error description (fallback only)
 */
export function getSmsFailureReason(
  errorCode?: string | number | null,
  errorMessage?: string | null,
): SmsFailureReason | null {
  const code = errorCode != null && `${errorCode}` !== '0' ? `${errorCode}` : null;
  if (code && REASONS[code]) return REASONS[code];

  // Known code we haven't mapped, or none at all but we do have a raw message.
  if (code) {
    return {
      label: 'Delivery failed',
      hint: errorMessage ? String(errorMessage) : 'The carrier could not deliver this message.',
      category: 'unknown',
    };
  }
  return null;
}

/**
 * Explanation for a text that is `sent` but NOT confirmed delivered.
 *
 * `sent` means the carrier accepted the message and never returned a delivery
 * receipt. It is not a failure — and, critically, it is FINAL: carriers do not
 * backfill receipts, so it will never become `delivered`. Showing it as
 * "pending" is a lie that 16 production rows told for up to 57 hours
 * (2026-07-29), leaving agents to assume a text was still on its way.
 *
 * Separate from getSmsFailureReason because this is NOT a failure — the UI
 * should style it neutrally, not red.
 */
export function getSmsSentUnconfirmedReason(): SmsFailureReason {
  return {
    label: 'Sent — delivery not confirmed',
    hint: "The carrier accepted this message but never reported delivery. It most likely arrived; carriers don't always send confirmations.",
    category: 'carrier',
  };
}

/** Short reason label for a badge/tooltip; empty string if none. */
export function getSmsFailureLabel(
  errorCode?: string | number | null,
  errorMessage?: string | null,
): string {
  return getSmsFailureReason(errorCode, errorMessage)?.label ?? '';
}
